<template>
  <div class="w-full">
    <div
      ref="containerRef"
      class="flex gap-0 items-center w-full border bg-white border-gray-300 rounded-[6px] shadow-sm transition-all duration-200 h-[38px]"
      :class="[
        {
          'border-red-300': error,
          'focus-within:ring-2 focus-within:ring-[#d0a444]/20 focus-within:border-[#d0a444]':
            !error,
        },
        containerClass,
      ]"
    >
      <!-- Country dropdown button -->
      <div class="relative shrink-0">
        <button
          ref="buttonRef"
          type="button"
          @click.stop="toggleDropdown"
          class="h-full px-[13px] py-[8px] flex items-center gap-1.5 sm:gap-2 hover:bg-gray-50 transition-colors border-r border-gray-300 rounded-l-[6px]"
          :class="buttonClass"
        >
          <span
            class="inline-flex items-center justify-center w-5 h-4.5 flex-shrink-0"
          >
            <span
              v-if="isDetectingCountry || !selected"
              class="w-5 h-4.5 bg-gray-200 animate-pulse"
            ></span>
            <span
              v-else
              :class="`fi fi-${selected.code}`"
              class="inline-block w-5 h-4.5"
            ></span>
          </span>
          <span
            class="text-base md:text-md text-gray-600 font-medium min-w-[21px]"
          >
            <span
              v-if="isDetectingCountry"
              class="inline-block w-5 rounded bg-gray-200 animate-pulse align-middle"
            ></span>
            <span v-else>{{ selected ? selected.value : "+1" }}</span>
          </span>
          <IconChevronDown
            class="w-2 h-2 text-slate-900 transition-transform rotate-180"
            :class="{ 'rotate-0': isOpen }"
          />
        </button>

        <!-- Country dropdown menu -->
        <Teleport to="body">
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div
              v-if="isOpen"
              ref="dropdownRef"
              :style="dropdownStyle"
              class="absolute bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-y-auto phone-input-dropdown"
              :class="highZIndex ? 'z-[10010]' : 'z-50'"
              :data-dropdown-id="dropdownId"
            >
              <!-- Search input -->
              <div
                class="z-10 sticky top-0 bg-white border-b border-gray-200 p-1.5 sm:p-2"
              >
                <input
                  ref="searchInputRef"
                  v-model="searchQuery"
                  type="text"
                  placeholder="Rechercher un pays..."
                  class="w-full px-3 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#d0a444]/20 focus:border-[#d0a444]"
                  @click.stop
                  @keydown.stop
                />
              </div>

              <!-- Country options -->
              <div v-if="filteredCountryOptions.length > 0">
                <div
                  v-for="option in filteredCountryOptions"
                  :key="option.value"
                  @click.stop="selectCountry(option)"
                  class="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-3 text-base transition-colors"
                  :class="{ 'bg-[#fff9ec]': country === option.value }"
                >
                  <span
                    :class="`fi fi-${option.code}`"
                    class="w-5 h-4.5 flex-shrink-0"
                  ></span>
                  <span class="flex-1">{{ option.label }}</span>
                </div>
              </div>

              <!-- No results message -->
              <div
                v-else
                class="px-2 sm:px-3 py-3 sm:py-4 text-base text-gray-500 text-center"
              >
                Aucun pays trouvé
              </div>
            </div>
          </Transition>
        </Teleport>
      </div>

      <!-- Phone input -->
      <input
        v-model="phone"
        type="tel"
        :placeholder="maskPlaceholder"
        class="flex-1 px-[13px] py-[8px] border-0 outline-none bg-transparent text-base leading-[20px]"
        :class="inputClass"
        @blur="onPhoneBlur"
        @input="onPhoneInput"
      />
    </div>

    <!-- Error messages -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <span
        v-if="error"
        class="block mt-1 text-[10px] sm:text-xs text-red-600"
        >{{ error }}</span
      >
    </Transition>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import IconChevronDown from "~/components/icons/IconChevronDown.vue";
import { logger } from "~/utils/logger";

interface Country {
  label: string;
  value: string;
  code: string; // ISO 3166-1 alpha-2 country code for flag
  mask: string;
  minLength: number;
  maxLength: number;
}

const countryOptions: Country[] = [
  // Europe
  {
    label: "France (+33)",
    value: "+33",
    code: "fr",
    mask: "6 12 34 56 78",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Allemagne (+49)",
    value: "+49",
    code: "de",
    mask: "151 23456789",
    minLength: 10,
    maxLength: 11,
  },
  {
    label: "Royaume-Uni (+44)",
    value: "+44",
    code: "gb",
    mask: "7400 123456",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Espagne (+34)",
    value: "+34",
    code: "es",
    mask: "612 34 56 78",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Italie (+39)",
    value: "+39",
    code: "it",
    mask: "320 1234567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Belgique (+32)",
    value: "+32",
    code: "be",
    mask: "470 12 34 56",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Pays-Bas (+31)",
    value: "+31",
    code: "nl",
    mask: "6 12345678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Suisse (+41)",
    value: "+41",
    code: "ch",
    mask: "78 123 45 67",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Portugal (+351)",
    value: "+351",
    code: "pt",
    mask: "912 345 678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Autriche (+43)",
    value: "+43",
    code: "at",
    mask: "664 123456",
    minLength: 10,
    maxLength: 13,
  },
  {
    label: "Grèce (+30)",
    value: "+30",
    code: "gr",
    mask: "691 234 5678",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Suède (+46)",
    value: "+46",
    code: "se",
    mask: "70 123 45 67",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Norvège (+47)",
    value: "+47",
    code: "no",
    mask: "406 12 345",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Danemark (+45)",
    value: "+45",
    code: "dk",
    mask: "32 12 34 56",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Finlande (+358)",
    value: "+358",
    code: "fi",
    mask: "41 2345678",
    minLength: 9,
    maxLength: 10,
  },
  {
    label: "Pologne (+48)",
    value: "+48",
    code: "pl",
    mask: "512 345 678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Irlande (+353)",
    value: "+353",
    code: "ie",
    mask: "85 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "République tchèque (+420)",
    value: "+420",
    code: "cz",
    mask: "601 123 456",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Roumanie (+40)",
    value: "+40",
    code: "ro",
    mask: "712 345 678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Hongrie (+36)",
    value: "+36",
    code: "hu",
    mask: "20 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Bulgarie (+359)",
    value: "+359",
    code: "bg",
    mask: "87 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Slovaquie (+421)",
    value: "+421",
    code: "sk",
    mask: "912 345 678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Croatie (+385)",
    value: "+385",
    code: "hr",
    mask: "91 234 5678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Luxembourg (+352)",
    value: "+352",
    code: "lu",
    mask: "621 123 456",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Slovénie (+386)",
    value: "+386",
    code: "si",
    mask: "31 234 567",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Estonie (+372)",
    value: "+372",
    code: "ee",
    mask: "5123 4567",
    minLength: 7,
    maxLength: 8,
  },
  {
    label: "Lettonie (+371)",
    value: "+371",
    code: "lv",
    mask: "21 234 567",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Lituanie (+370)",
    value: "+370",
    code: "lt",
    mask: "612 34567",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Serbie (+381)",
    value: "+381",
    code: "rs",
    mask: "60 1234567",
    minLength: 8,
    maxLength: 9,
  },
  {
    label: "Ukraine (+380)",
    value: "+380",
    code: "ua",
    mask: "50 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Russie (+7)",
    value: "+7",
    code: "ru",
    mask: "912 345-67-89",
    minLength: 10,
    maxLength: 10,
  },

  // Afrique du Nord et Moyen-Orient
  {
    label: "Maroc (+212)",
    value: "+212",
    code: "ma",
    mask: "612-345678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Algérie (+213)",
    value: "+213",
    code: "dz",
    mask: "551 23 45 67",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Tunisie (+216)",
    value: "+216",
    code: "tn",
    mask: "20 123 456",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Égypte (+20)",
    value: "+20",
    code: "eg",
    mask: "100 123 4567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Libye (+218)",
    value: "+218",
    code: "ly",
    mask: "91 2345678",
    minLength: 9,
    maxLength: 10,
  },
  {
    label: "Turquie (+90)",
    value: "+90",
    code: "tr",
    mask: "501 234 5678",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Arabie Saoudite (+966)",
    value: "+966",
    code: "sa",
    mask: "50 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Émirats arabes unis (+971)",
    value: "+971",
    code: "ae",
    mask: "50 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Qatar (+974)",
    value: "+974",
    code: "qa",
    mask: "3312 3456",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Koweït (+965)",
    value: "+965",
    code: "kw",
    mask: "500 12345",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Bahreïn (+973)",
    value: "+973",
    code: "bh",
    mask: "3600 1234",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Oman (+968)",
    value: "+968",
    code: "om",
    mask: "9123 4567",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Liban (+961)",
    value: "+961",
    code: "lb",
    mask: "3 123 456",
    minLength: 7,
    maxLength: 8,
  },
  {
    label: "Jordanie (+962)",
    value: "+962",
    code: "jo",
    mask: "7 9012 3456",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Syrie (+963)",
    value: "+963",
    code: "sy",
    mask: "944 123 456",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Iraq (+964)",
    value: "+964",
    code: "iq",
    mask: "790 123 4567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Yémen (+967)",
    value: "+967",
    code: "ye",
    mask: "712 345 678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Palestine (+970)",
    value: "+970",
    code: "ps",
    mask: "599 123 456",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Israël (+972)",
    value: "+972",
    code: "il",
    mask: "50 123 4567",
    minLength: 9,
    maxLength: 9,
  },

  // Afrique subsaharienne
  {
    label: "Sénégal (+221)",
    value: "+221",
    code: "sn",
    mask: "77 123 45 67",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Côte d'Ivoire (+225)",
    value: "+225",
    code: "ci",
    mask: "01 23 45 67",
    minLength: 8,
    maxLength: 10,
  },
  {
    label: "Mali (+223)",
    value: "+223",
    code: "ml",
    mask: "65 12 34 56",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Niger (+227)",
    value: "+227",
    code: "ne",
    mask: "93 12 34 56",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Burkina Faso (+226)",
    value: "+226",
    code: "bf",
    mask: "70 12 34 56",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Guinée (+224)",
    value: "+224",
    code: "gn",
    mask: "601 12 34 56",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Cameroun (+237)",
    value: "+237",
    code: "cm",
    mask: "6 71 23 45 67",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Nigeria (+234)",
    value: "+234",
    code: "ng",
    mask: "802 123 4567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Ghana (+233)",
    value: "+233",
    code: "gh",
    mask: "23 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Kenya (+254)",
    value: "+254",
    code: "ke",
    mask: "712 345678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Afrique du Sud (+27)",
    value: "+27",
    code: "za",
    mask: "71 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Éthiopie (+251)",
    value: "+251",
    code: "et",
    mask: "91 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Tanzanie (+255)",
    value: "+255",
    code: "tz",
    mask: "621 234 567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Ouganda (+256)",
    value: "+256",
    code: "ug",
    mask: "712 345678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Madagascar (+261)",
    value: "+261",
    code: "mg",
    mask: "32 12 345 67",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Maurice (+230)",
    value: "+230",
    code: "mu",
    mask: "5251 2345",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "RD Congo (+243)",
    value: "+243",
    code: "cd",
    mask: "991 234 567",
    minLength: 9,
    maxLength: 9,
  },

  // Amériques
  {
    label: "États-Unis (+1)",
    value: "+1",
    code: "us",
    mask: "(555) 123-4567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Canada (+1)",
    value: "+1",
    code: "ca",
    mask: "(555) 123-4567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Mexique (+52)",
    value: "+52",
    code: "mx",
    mask: "55 1234 5678",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Brésil (+55)",
    value: "+55",
    code: "br",
    mask: "11 91234-5678",
    minLength: 10,
    maxLength: 11,
  },
  {
    label: "Argentine (+54)",
    value: "+54",
    code: "ar",
    mask: "11 2345-6789",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Chili (+56)",
    value: "+56",
    code: "cl",
    mask: "9 1234 5678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Colombie (+57)",
    value: "+57",
    code: "co",
    mask: "321 1234567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Pérou (+51)",
    value: "+51",
    code: "pe",
    mask: "912 345 678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Venezuela (+58)",
    value: "+58",
    code: "ve",
    mask: "412 1234567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Équateur (+593)",
    value: "+593",
    code: "ec",
    mask: "99 123 4567",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Bolivie (+591)",
    value: "+591",
    code: "bo",
    mask: "7 1234567",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Uruguay (+598)",
    value: "+598",
    code: "uy",
    mask: "94 123 456",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Paraguay (+595)",
    value: "+595",
    code: "py",
    mask: "961 123456",
    minLength: 9,
    maxLength: 9,
  },

  // Asie
  {
    label: "Chine (+86)",
    value: "+86",
    code: "cn",
    mask: "131 1234 5678",
    minLength: 11,
    maxLength: 11,
  },
  {
    label: "Inde (+91)",
    value: "+91",
    code: "in",
    mask: "81234 56789",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Japon (+81)",
    value: "+81",
    code: "jp",
    mask: "90 1234 5678",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Corée du Sud (+82)",
    value: "+82",
    code: "kr",
    mask: "10 1234 5678",
    minLength: 9,
    maxLength: 10,
  },
  {
    label: "Pakistan (+92)",
    value: "+92",
    code: "pk",
    mask: "300 1234567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Bangladesh (+880)",
    value: "+880",
    code: "bd",
    mask: "1712-345678",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Philippines (+63)",
    value: "+63",
    code: "ph",
    mask: "905 123 4567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Vietnam (+84)",
    value: "+84",
    code: "vn",
    mask: "91 234 56 78",
    minLength: 9,
    maxLength: 10,
  },
  {
    label: "Thaïlande (+66)",
    value: "+66",
    code: "th",
    mask: "81 234 5678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Indonésie (+62)",
    value: "+62",
    code: "id",
    mask: "812 3456 7890",
    minLength: 10,
    maxLength: 12,
  },
  {
    label: "Malaisie (+60)",
    value: "+60",
    code: "my",
    mask: "12-345 6789",
    minLength: 9,
    maxLength: 10,
  },
  {
    label: "Singapour (+65)",
    value: "+65",
    code: "sg",
    mask: "8123 4567",
    minLength: 8,
    maxLength: 8,
  },
  {
    label: "Sri Lanka (+94)",
    value: "+94",
    code: "lk",
    mask: "71 234 5678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Népal (+977)",
    value: "+977",
    code: "np",
    mask: "984-1234567",
    minLength: 10,
    maxLength: 10,
  },
  {
    label: "Myanmar (+95)",
    value: "+95",
    code: "mm",
    mask: "9 123 456 789",
    minLength: 8,
    maxLength: 10,
  },
  {
    label: "Cambodge (+855)",
    value: "+855",
    code: "kh",
    mask: "12 345 678",
    minLength: 8,
    maxLength: 9,
  },
  {
    label: "Laos (+856)",
    value: "+856",
    code: "la",
    mask: "20 1234 5678",
    minLength: 10,
    maxLength: 10,
  },

  // Océanie
  {
    label: "Australie (+61)",
    value: "+61",
    code: "au",
    mask: "412 345 678",
    minLength: 9,
    maxLength: 9,
  },
  {
    label: "Nouvelle-Zélande (+64)",
    value: "+64",
    code: "nz",
    mask: "21 123 4567",
    minLength: 9,
    maxLength: 10,
  },
  {
    label: "Fidji (+679)",
    value: "+679",
    code: "fj",
    mask: "701 2345",
    minLength: 7,
    maxLength: 7,
  },
  {
    label: "Papouasie-Nouvelle-Guinée (+675)",
    value: "+675",
    code: "pg",
    mask: "7012 3456",
    minLength: 8,
    maxLength: 8,
  },
];

const props = defineProps<{
  modelValue?: { country?: string; phone?: string } | null;
  required?: boolean;
  highZIndex?: boolean;
  error?: string;
  containerClass?: string;
  buttonClass?: string;
  inputClass?: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: { country: string; phone: string } | null];
  validity: [isValid: boolean];
}>();

const country = ref(props.modelValue?.country || "");
const phone = ref(props.modelValue?.phone || "");
const isDetectingCountry = ref(!props.modelValue?.phone);

const errorCountry = ref("");
const errorPhone = ref("");
const isOpen = ref(false);
const searchQuery = ref("");
const containerRef = ref<HTMLElement | null>(null);
const buttonRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);
const hasInteracted = ref(false);
const dropdownId = `phone-dropdown-${Math.random().toString(36).substr(2, 9)}`;

const selected = computed(() =>
  countryOptions.find((opt) => opt.value === country.value),
);

const maskPlaceholder = computed(() => {
  return selected.value ? selected.value.mask : "123-456-7890";
});

const filteredCountryOptions = computed(() => {
  if (!searchQuery.value.trim()) {
    return countryOptions;
  }

  const query = searchQuery.value.toLowerCase().trim();
  return countryOptions.filter((option) => {
    return (
      option.label.toLowerCase().includes(query) ||
      option.value.includes(query) ||
      option.code.toLowerCase().includes(query)
    );
  });
});

const dropdownStyle = ref({});

const toggleDropdown = (event?: Event) => {
  if (event) {
    event.stopPropagation();
  }
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchQuery.value = ""; // Clear search when opening
    updateDropdownPosition();
    // Focus search input after dropdown opens
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  }
};

const selectCountry = (option: Country) => {
  country.value = option.value;
  searchQuery.value = ""; // Clear search when selecting
  isOpen.value = false;

  // Reformat phone number for new country
  if (phone.value) {
    const digitsOnly = phone.value.replace(/\D/g, "");
    phone.value = formatPhoneNumber(digitsOnly);
  }
};

const updateDropdownPosition = () => {
  if (!containerRef.value) return;

  const rect = containerRef.value.getBoundingClientRect();
  const width = rect.width;

  dropdownStyle.value = {
    top: `${rect.bottom + window.scrollY + 4}px`,
    left: `${rect.left + window.scrollX}px`,
    width: `${width}px`,
  };
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  if (!containerRef.value) return;

  // Check if click is outside container and dropdown
  if (!containerRef.value.contains(target)) {
    const dropdown = document.querySelector(
      `[data-dropdown-id="${dropdownId}"]`,
    );
    if (!dropdown || !dropdown.contains(target)) {
      isOpen.value = false;
    }
  }
};

onMounted(async () => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", updateDropdownPosition);
  window.addEventListener("scroll", updateDropdownPosition, true);

  // Format initial phone number if provided
  if (phone.value) {
    const digitsOnly = phone.value.replace(/\D/g, "");
    phone.value = formatPhoneNumber(digitsOnly);
  }

  // Auto-detect country if no phone number has been entered yet
  if (!props.modelValue?.phone) {
    isDetectingCountry.value = true;
    await detectUserCountry();
    isDetectingCountry.value = false;
  } else if (!country.value) {
    country.value = "+32";
  }
});

// Auto-detect user's country using server-side geolocation API
async function detectUserCountry() {
  try {
    const config = useRuntimeConfig();

    // Use backend API if configured, otherwise use in-repo server function
    const backendUrl = `${config.public.apiBase}/geo/detect`;
    const localUrl = "/api/geo/detect";

    let response: {
      success: boolean;
      countryCode: string;
      default?: boolean;
    } | null = null;

    const preferLocal =
      !config.public.useBackend || config.public.apiBase?.includes("localhost");

    try {
      response = await $fetch<{ success: boolean; countryCode: string }>(
        localUrl,
      );
    } catch (error) {
      if (!preferLocal) {
        response = await $fetch<{ success: boolean; countryCode: string }>(
          backendUrl,
        );
      } else {
        throw error;
      }
    }

    let countryCode: string | undefined =
      response?.countryCode?.toLowerCase() ?? undefined;

    if (!countryCode || response?.default) {
      countryCode = (await detectUserCountryClient()) ?? undefined;
    }

    if (countryCode) {
      // Find matching country in our options
      const matchedCountry = countryOptions.find(
        (opt) => opt.code === countryCode,
      );

      if (matchedCountry && !props.modelValue?.phone) {
        // Only set if user hasn't already entered a phone number
        country.value = matchedCountry.value;
        emit("update:modelValue", {
          country: matchedCountry.value,
          phone: phone.value,
        });
      }
    } else if (!country.value && !props.modelValue?.phone) {
      country.value = "+32";
    }
  } catch (error) {
    // Silently fail - keep default country
    logger.debug("[PhoneInput] Country detection failed, using default");
    if (!country.value && !props.modelValue?.phone) {
      country.value = "+32";
    }
  }
}

async function detectUserCountryClient(): Promise<string | null> {
  // Use the server-side geo endpoint (safer & not blocked by CORS/IP restrictions)
  try {
    const res = await $fetch<{ success?: boolean; countryCode?: string }>(
      "/api/geo/detect",
      { method: "GET", credentials: "include", retry: 0, timeout: 3000 },
    );

    if (res?.success && res.countryCode) {
      return res.countryCode.toLowerCase();
    }
  } catch (error) {
    logger.debug("[PhoneInput] Client geo lookup (server) failed", error);
  }

  return null;
}

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", updateDropdownPosition);
  window.removeEventListener("scroll", updateDropdownPosition, true);
});

// Watch for external prop changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      if (newValue.country && newValue.country !== country.value) {
        country.value = newValue.country;
      }
      if (newValue.phone !== undefined) {
        const digitsOnly = newValue.phone.replace(/\D/g, "");
        const formatted = formatPhoneNumber(digitsOnly);
        if (formatted !== phone.value) {
          phone.value = formatted;
        }
      }
    }
  },
  { deep: true },
);

watch(
  () => [country.value, phone.value],
  () => {
    if (isDetectingCountry.value) return;
    emit("update:modelValue", {
      country: country.value,
      phone: phone.value,
    });
    if (hasInteracted.value) {
      validatePhone();
    }
  },
);

function formatPhoneNumber(value: string): string {
  if (!selected.value) return value;

  // Get only digits
  const digitsOnly = value.replace(/\D/g, "");

  // Get the mask pattern
  const mask = selected.value.mask;

  let formatted = "";
  let digitIndex = 0;

  // Apply mask character by character
  for (let i = 0; i < mask.length && digitIndex < digitsOnly.length; i++) {
    const maskChar = mask[i];

    // If mask character is a digit, replace with actual digit
    if (maskChar && /\d/.test(maskChar)) {
      formatted += digitsOnly[digitIndex];
      digitIndex++;
    } else if (maskChar) {
      // If mask character is a separator (space, dash, parenthesis, etc.)
      formatted += maskChar;
    }
  }

  // If there are remaining digits beyond the mask, append them
  if (digitIndex < digitsOnly.length) {
    formatted += digitsOnly.substring(digitIndex);
  }

  return formatted;
}

function onPhoneInput(event: Event) {
  // Just track interaction state, errors are controlled by parent
  hasInteracted.value = true;

  const input = event.target as HTMLInputElement;
  const cursorPosition = input.selectionStart || 0;
  const oldValue = phone.value;
  const newValue = input.value;

  // Get digits only from new value
  const digitsOnly = newValue.replace(/\D/g, "");

  // Apply formatting
  const formatted = formatPhoneNumber(digitsOnly);

  // Calculate new cursor position
  let newCursorPosition = cursorPosition;

  // If formatted value changed, adjust cursor
  if (formatted !== newValue) {
    // Count non-digit characters before cursor in old value
    const nonDigitsBeforeCursor = oldValue
      .substring(0, cursorPosition)
      .replace(/\d/g, "").length;
    // Count non-digit characters in formatted value up to the same digit count
    const digitsBeforeCursor = oldValue
      .substring(0, cursorPosition)
      .replace(/\D/g, "").length;

    // Find position in formatted string
    let digitCount = 0;
    newCursorPosition = 0;
    for (
      let i = 0;
      i < formatted.length && digitCount < digitsBeforeCursor;
      i++
    ) {
      const char = formatted[i];
      if (char && /\d/.test(char)) {
        digitCount++;
      }
      newCursorPosition = i + 1;
    }
  }

  phone.value = formatted;

  validatePhone();

  // Restore cursor position
  nextTick(() => {
    input.setSelectionRange(newCursorPosition, newCursorPosition);
  });
}

function onPhoneBlur() {
  hasInteracted.value = true;
  validatePhone();
}

function validatePhone() {
  // Just emit validity, don't set internal errors
  // Parent controls error display via :error prop

  if (props.required && !country.value) {
    emit("validity", false);
    return;
  }

  if (!phone.value) {
    emit("validity", !props.required);
    return;
  }

  // Validate phone length based on country
  const digitsOnly = phone.value.replace(/\D/g, "");
  const normalizedDigits =
    digitsOnly.startsWith("0") && digitsOnly.length > 1
      ? digitsOnly.substring(1)
      : digitsOnly;

  if (selected.value) {
    const { minLength, maxLength } = selected.value;

    const withinRange =
      normalizedDigits.length >= minLength &&
      normalizedDigits.length <= maxLength;

    if (!withinRange) {
      emit("validity", false);
      return;
    }
  } else if (normalizedDigits.length < 6) {
    emit("validity", false);
    return;
  }

  emit("validity", true);
}
</script>
